import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, KeyRound, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import { buildSupportWhatsAppUrl } from "@/lib/supportContact";
import VideoTutorialButton from "@/components/poshan/VideoTutorialButton";
import LoginLanding from "@/components/poshan/LoginLanding";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import FloatingLabelInput from "@/components/mobile/FloatingLabelInput";

export default function Login() {
  const { user, login, ready } = useAuth();
  const nav = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!ready) return null;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mobile || !password) { toast.error("Enter mobile number and password"); return; }
    if (!/^\d{10}$/.test(mobile)) { toast.error("Mobile number must be exactly 10 digits"); return; }
    setBusy(true);
    try {
      const u = await login(mobile.trim(), password);
      // iter121 — refer to school by friendly name only; never surface UDISE on this page.
      const friendly = u.schoolName || (u.role === "admin" ? "Admin" : "Headmaster");
      toast.success(`Welcome back, ${friendly}`);
      nav(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const handleForgotPassword = () => {
    // Pre-fill a WhatsApp message to the platform admin. iter121: removed the
    // explicit "UDISE Code" line from the prefill so this page never mentions
    // UDISE; the admin can look up the account by mobile + school name alone.
    const lines = [
      "Hi MDM Sahayak Support,",
      "",
      "I forgot my login password. Please reset it for me.",
      "",
      `My Mobile Number: ${mobile || "____________"}`,
      "School Name: ____________",
      "",
      "Thank you.",
    ];
    const url = buildSupportWhatsAppUrl(lines.join("\n"));
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <main className="min-h-screen bg-sand-50 px-4 py-8 text-ink-900 sm:px-6 sm:py-12">
      <div className="mx-auto w-full max-w-5xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <img src="/logo192.png" alt="MDM Sahayak logo" className="mx-auto h-16 w-16 rounded-xl shadow-sm" />
          <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">MDM Sahayak</h1>
          <p className="mt-2 text-sm text-ink-700">
            Independent digital productivity &amp; documentation assistance platform
          </p>
        </div>

        {/* iter129 — prominent independence disclaimer above the fold on the
            login screen (the SiteFooter already shows it globally, but
            visitors landing on /login should see it before they sign in). */}
        <div className="mb-6">
          <DisclaimerBanner variant="compact" />
        </div>

        {/* Login card + tutorial CTAs — centered, capped at max-w-md so the
            form stays comfortable on every screen size. */}
        <div className="mx-auto w-full max-w-md">
          <form onSubmit={handleSubmit} className="rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6" data-testid="login-form">
            <h2 className="flex items-center gap-2 text-xl font-semibold"><LogIn className="h-5 w-5 text-clay" /> User Login</h2>
            <p className="mt-1 text-xs text-ink-700">Sign in with your registered mobile number.</p>
            <div className="mt-5 space-y-4">
              {/* iter137 — floating-label inputs with larger touch targets. */}
              <FloatingLabelInput
                id="mobile"
                label="Mobile Number"
                inputMode="numeric"
                autoComplete="tel"
                autoFocus
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                maxLength={10}
                testid="login-mobile-input"
              />
              <FloatingLabelInput
                id="pw"
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                testid="login-password-input"
              />
            </div>
            <Button type="submit" disabled={busy} className="mt-6 h-12 w-full bg-clay text-base font-bold hover:bg-clay/90" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </Button>

            {/* Forgot password — opens WhatsApp chat with the platform admin so
                they can manually verify identity and reset from Admin Dashboard. */}
            <button
              type="button"
              onClick={handleForgotPassword}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100"
              data-testid="login-forgot-password-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4" />
              Forgot Password? Recover via WhatsApp
            </button>

            <p className="mt-4 text-center text-sm text-ink-700">
              New school? <Link to="/register" className="font-semibold text-clay hover:underline" data-testid="login-register-link">Register here</Link>
            </p>
            <p className="mt-2 flex items-center justify-center gap-1 text-center text-[11px] text-ink-700">
              <KeyRound className="h-3 w-3" /> Login is by registered mobile number + password.
            </p>
          </form>

          {/* Odia tutorial CTAs — bare YouTube URLs stay hidden; videos play
              inside an in-page modal. */}
          <div className="mt-5 space-y-3" data-testid="login-tutorials">
            <VideoTutorialButton
              youtubeId="VWrMfzv-81M"
              titleOdia="🎬 ଭିଡିଓ ଟ୍ୟୁଟୋରିଆଲ୍ ଦେଖନ୍ତୁ"
              subtitleOdia="Account କିପରି ତିଆରି କରିବେ — Step by Step ଭିଡିଓ"
              dialogTitleOdia="MDM Sahayak — Account Creation"
              durationLabel="▶ Watch Now"
              variant="sunset"
              testId="login-video-tutorial"
            />
            <VideoTutorialButton
              youtubeId="T3Ym27t4Rhc"
              titleOdia="📊 MDM Bill · UC · Hand Receipts କିପରି ତିଆରି ହେବ"
              subtitleOdia="Portal ରେ ସମ୍ପୂର୍ଣ୍ଣ ପ୍ରକ୍ରିୟା — Live Demo"
              dialogTitleOdia="MDM Bill, UC & Hand Receipts on the Portal"
              durationLabel="▶ Full Walkthrough"
              variant="moss"
              testId="login-mdm-walkthrough"
            />
          </div>
        </div>

        {/* iter121 — high-conversion landing section below the login form
            (social proof, benefits, plans, testimonials, FAQ, final CTA). */}
        <LoginLanding />

        {/* SiteFooter (mounted in App.js) already renders the "Independent
            Platform Notice" globally, so we don't repeat it here. */}
      </div>
    </main>
  );
}
